import { useEffect, useMemo, useRef, useState } from 'react';

import { useGetUserPlanAvailability } from '@/api/gen/quiz-plan-payment/quiz-plan-payment.gen';
import { useUser } from '@/lib/auth';

import {
  NO_TRIAL_END_DISCOUNT,
  readTrialEndDiscount,
  resolveTrialEndDiscountState,
  type TrialEndDiscountState,
} from './trialEndDiscount';

/** 카운트다운이 분 단위라 1분마다만 다시 판정한다. */
const TICK_MS = 60_000;

/**
 * 체험 종료 후 7일 할인 자격 훅.
 *
 * 첫 구매 할인과 같은 availability 응답을 사용하되, 플랜 전환·재진입 때 자격을 다시 확인한다.
 */
export function useTrialEndDiscount(): TrialEndDiscountState {
  const { isAuthenticated, user } = useUser();
  const enabled =
    isAuthenticated &&
    (user?.planType === 'FREE' || user?.planSourceType === 'VOUCHER');

  const { data, refetch } = useGetUserPlanAvailability({
    query: {
      enabled,
      staleTime: 0,
      refetchOnWindowFocus: true,
      retry: 0,
      throwOnError: false,
      meta: { ignoreError: true },
    },
  });

  // enabled=false도 기존 캐시를 반환하므로 현재 플랜의 노출 조건을 별도로 적용한다.
  const info = enabled ? readTrialEndDiscount(data?.data) : null;
  const trialEndAtMs = info ? Date.parse(info.trialEndAt) : null;
  const windowEndMs = info ? Date.parse(info.windowEnd) : null;
  const [nowMs, setNowMs] = useState(() => Date.now());
  const hasRefetchedAfterTrialEnd = useRef(false);
  const wasBeforeTrialEnd = useRef(false);

  useEffect(() => {
    hasRefetchedAfterTrialEnd.current = false;
    wasBeforeTrialEnd.current = false;
  }, [trialEndAtMs]);

  useEffect(() => {
    if (trialEndAtMs !== null && nowMs < trialEndAtMs) {
      wasBeforeTrialEnd.current = true;
    }
  }, [nowMs, trialEndAtMs]);

  useEffect(() => {
    if (
      trialEndAtMs === null ||
      windowEndMs === null ||
      !Number.isFinite(trialEndAtMs) ||
      !Number.isFinite(windowEndMs)
    ) {
      return;
    }
    let timer: ReturnType<typeof setTimeout>;
    const tick = () => {
      const now = Date.now();
      setNowMs(now);
      if (
        now >= trialEndAtMs &&
        now < windowEndMs &&
        wasBeforeTrialEnd.current &&
        !hasRefetchedAfterTrialEnd.current
      ) {
        hasRefetchedAfterTrialEnd.current = true;
        void refetch();
      }
      const nextBoundaryMs = now < trialEndAtMs ? trialEndAtMs : windowEndMs;
      if (nextBoundaryMs > now) {
        timer = setTimeout(tick, Math.min(TICK_MS, nextBoundaryMs - now));
      }
    };
    tick();
    return () => clearTimeout(timer);
  }, [refetch, trialEndAtMs, windowEndMs]);

  return useMemo(
    () =>
      info ? resolveTrialEndDiscountState(info, nowMs) : NO_TRIAL_END_DISCOUNT,
    [info, nowMs],
  );
}
